import { mapState } from 'vuex';
const maxZoomFactor = 3.0;
const minZoomFactor = 1.0;
export default {
  name: 'CoreDtwinMapOverlayWizardImageAnchorPicker',
  props: {
    imageSrc: {
      type: String,
      required: true
    },
    height: {
      type: Number,
      required: true
    },
    width: {
      type: Number,
      required: true
    }
  },
  data: function () {
    return {
      zoom: 1.0,
      image: {
        htmlEl: null,
        orgDimension: {}
      },
      canvasData: {
        baseDimension: {},
        offsetPercent: {
          x: 0,
          y: 0
        }
      },
      dragging: {
        start: {},
        // do not react to an "click" event if the canvas was just dragged
        didDrag: false
      },
      anchor1: {
        visible: false,
        basePosition: {},
        displayPosition: {}
      },
      anchor2: {
        visible: false,
        basePosition: {},
        displayPosition: {}
      }
    };
  },
  computed: {
    ...mapState('app', ['inpOutlined', 'inpFilled', 'color', 'permissions', 'UiTheme']),
    modeClass() {
      return this.UiTheme ? 'dark' : 'light';
    },
    maxZoomFactor() {
      return maxZoomFactor;
    },
    minZoomFactor() {
      return minZoomFactor;
    }
  },
  watch: {
    width() {
      this.handleZoomChange();
      this.drawOverlayAtOffset();
    },
    zoom() {
      this.handleZoomChange();
      this.drawOverlayAtOffset();
    },
    'anchor1.basePosition'() {
      if (this.anchor1.visible) {
        this.anchor1.displayPosition = this.convertToCanvasPos(this.anchor1.basePosition);
      }
    },
    'anchor2.basePosition'() {
      if (this.anchor2.visible) {
        this.anchor2.displayPosition = this.convertToCanvasPos(this.anchor2.basePosition);
      }
    }
  },
  mounted() {
    this.anchor1.visible = false;
    this.anchor2.visible = false;
    this.drawImage();
  },
  methods: {
    drawImage() {
      this.image.htmlEl = new Image();
      // https://stackoverflow.com/questions/22710627/tainted-canvases-may-not-be-exported
      // avoid security-errors from resource-origins during canvas-drawing-export of images
      this.image.htmlEl.crossOrigin = 'Anonymous';
      this.image.htmlEl.src = this.imageSrc;
      const context = this;
      this.image.htmlEl.onload = () => {
        context.image.orgDimension.width = context.image.htmlEl.naturalWidth;
        context.image.orgDimension.height = context.image.htmlEl.naturalHeight;
        context.handleZoomChange();
        context.drawOverlayAtOffset();
      };
    },
    zoomIn() {
      this.zoom = Math.min(maxZoomFactor, this.zoom + 0.1);
    },
    zoomOut() {
      this.zoom = Math.max(minZoomFactor, this.zoom - 0.1);
    },
    handleZoomChange() {
      const translatedCanvasDimension = {
        width: this.width * this.zoom,
        height: this.height * this.zoom
      };
      const targetDimension = {
        width: this.image.orgDimension.width / (this.image.orgDimension.height / translatedCanvasDimension.width),
        height: this.image.orgDimension.height / (this.image.orgDimension.width / translatedCanvasDimension.height)
      };
      // choose the canvas-dimensions according to the least amount of necessary zoom
      if (targetDimension.height < translatedCanvasDimension.height) {
        this.canvasData.baseDimension = {
          width: translatedCanvasDimension.width,
          height: targetDimension.height
        };
      } else {
        this.canvasData.baseDimension = {
          width: targetDimension.width,
          height: translatedCanvasDimension.height
        };
      }
    },
    drawOverlayAtOffset() {
      const translatedOffsetX = this.image.orgDimension.width * this.canvasData.offsetPercent.x;
      const translatedOffsetY = this.image.orgDimension.height * this.canvasData.offsetPercent.y;

      // https://www.w3schools.com/tags/canvas_drawimage.asp
      const canvas = this.$refs.canvas;
      canvas.width = this.width;
      canvas.height = this.height;
      let canvasCtx = canvas.getContext('2d');
      if (this.image.orgDimension.width !== undefined && this.image.orgDimension.height !== undefined && !isNaN(this.canvasData.baseDimension.width) && !isNaN(this.canvasData.baseDimension.height)) {
        canvasCtx.drawImage(this.image.htmlEl, translatedOffsetX, translatedOffsetY, this.image.orgDimension.width, this.image.orgDimension.height, 0, 0, this.canvasData.baseDimension.width, this.canvasData.baseDimension.height);
      }
      if (this.anchor1.visible) {
        this.anchor1.displayPosition = this.convertToCanvasPos(this.anchor1.basePosition);
      }
      if (this.anchor2.visible) {
        this.anchor2.displayPosition = this.convertToCanvasPos(this.anchor2.basePosition);
      }
    },
    onDragStart(e) {
      const canvas = this.$refs.canvas;
      this.dragging.start = {
        x: e.pageX - canvas.offsetLeft,
        y: e.pageY - canvas.offsetTop
      };
    },
    onDrag(e) {
      if (this.dragging.start.x && this.dragging.start.y) {
        this.didDrag = true;
        const canvas = this.$refs.canvas;
        let movedPixelsX = e.pageX - canvas.offsetLeft - this.dragging.start.x;
        let movedPixelsY = e.pageY - canvas.offsetTop - this.dragging.start.y;
        this.canvasData.offsetPercent.x -= movedPixelsX / this.canvasData.baseDimension.width;
        this.canvasData.offsetPercent.y -= movedPixelsY / this.canvasData.baseDimension.height;
        this.canvasData.offsetPercent.x = Math.min(1, Math.max(0, this.canvasData.offsetPercent.x));
        this.canvasData.offsetPercent.y = Math.min(1, Math.max(0, this.canvasData.offsetPercent.y));
        this.onDragStart(e);
        this.drawOverlayAtOffset();
      }
    },
    onDragEnd() {
      this.dragging.start = {};
    },
    onCanvasClick(e) {
      if (this.anchor1.visible && this.anchor2.visible) {
        return;
      }
      if (this.didDrag) {
        this.didDrag = false;
        return;
      }
      const converted = this.convertFromCanvasPos({
        x: e.offsetX,
        y: e.offsetY
      });
      if (!this.anchor1.visible) {
        this.updateAnchor1(converted.x, converted.y);
      } else if (!this.anchor2.visible) {
        this.updateAnchor2(converted.x, converted.y);
      }
    },
    anchor1Moved(position) {
      let xStretchFactor = this.image.orgDimension.width / this.canvasData.baseDimension.width;
      let yStretchFactor = this.image.orgDimension.height / this.canvasData.baseDimension.height;
      this.updateAnchor1(this.anchor1.basePosition.x + position.offsetX * xStretchFactor, this.anchor1.basePosition.y + position.offsetY * yStretchFactor);
    },
    anchor2Moved(position) {
      let xStretchFactor = this.image.orgDimension.width / this.canvasData.baseDimension.width;
      let yStretchFactor = this.image.orgDimension.height / this.canvasData.baseDimension.height;
      this.updateAnchor2(this.anchor2.basePosition.x + position.offsetX * xStretchFactor, this.anchor2.basePosition.y + position.offsetY * yStretchFactor);
    },
    updateAnchor1(x, y) {
      this.anchor1.visible = true;
      this.anchor1.basePosition = {
        x,
        y
      };
      this.$emit('update-anchor1-position', this.anchor1.basePosition);
    },
    updateAnchor2(x, y) {
      this.anchor2.visible = true;
      this.anchor2.basePosition = {
        x,
        y
      };
      this.$emit('update-anchor2-position', this.anchor2.basePosition);
    },
    convertFromCanvasPos(pos) {
      return {
        x: this.convertFromCanvasPosInDimension(pos.x, 'x', 'width'),
        y: this.convertFromCanvasPosInDimension(pos.y, 'y', 'height')
      };
    },
    convertFromCanvasPosInDimension(value, offsetDimension, dimension) {
      const stretch = this.image.orgDimension[dimension] / this.canvasData.baseDimension[dimension] / this.zoom;
      return (this.canvasData.offsetPercent[offsetDimension] * this.canvasData.baseDimension[dimension] * this.zoom + value * this.zoom) * stretch;
    },
    convertToCanvasPos(pos) {
      return {
        x: this.convertToCanvasPosInDimension(pos.x, 'x', 'width'),
        y: this.convertToCanvasPosInDimension(pos.y, 'y', 'height')
      };
    },
    convertToCanvasPosInDimension(value, offsetDimension, dimension) {
      const stretch = this.image.orgDimension[dimension] / this.canvasData.baseDimension[dimension] / this.zoom;
      return (value / stretch - this.canvasData.offsetPercent[offsetDimension] * this.canvasData.baseDimension[dimension] * this.zoom) / this.zoom;
    }
  }
};